import type { Cents, Job } from "@roof-hawk/core";

import { useRememberedState } from "../../lib/hooks";
import { CallsReport } from "./CallsReport";
import { WarrantiesReport } from "./WarrantiesReport";

const VIEWS = [
  ["calls", "Calls"],
  ["warranties", "Warranties"],
] as const;
type View = (typeof VIEWS)[number][0];

/**
 * The Reports tab: what the phone numbers bought, and where the two
 * manufacturer certifications stand (ADR 0309).
 *
 * The section somebody was on is remembered, the way Marketing remembers
 * its own; anything unrecognised falls back to Calls, which the tab was
 * before it had sections.
 */
export function ReportsScreen({
  jobs,
  values,
  /** A section the page was asked for, which beats the remembered one. */
  section = null,
}: {
  jobs: readonly Job[];
  values: ReadonlyMap<string, Cents> | null;
  section?: View | null;
}) {
  const [remembered, setView] = useRememberedState("reports", "calls", section);
  const view: View =
    VIEWS.find(([value]) => value === remembered)?.[0] ?? "calls";

  return (
    <>
      <div className="viewswitch" role="tablist" aria-label="Reports">
        {VIEWS.map(([value, label]) => (
          <button
            key={value}
            type="button"
            role="tab"
            aria-selected={view === value}
            className={view === value ? "on" : ""}
            onClick={() => setView(value)}
          >
            {label}
          </button>
        ))}
      </div>
      {view === "warranties" ? (
        <WarrantiesReport />
      ) : (
        <CallsReport jobs={jobs} values={values} />
      )}
    </>
  );
}
